import { useState } from 'react';
import { useNavigate, Link, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Buildings, EnvelopeSimple, Lock, User, ArrowRight, Key, Hourglass } from '@phosphor-icons/react';
import LegalFooter from '../components/LegalFooter';

function formatApiErrorDetail(detail) {
  if (detail == null) return "Etwas ist schief gelaufen. Bitte versuchen Sie es erneut.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).filter(Boolean).join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

export default function Login() {
  const [mode, setMode] = useState('admin'); // 'admin' or 'tenant'
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const { login, portalLogin } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const idleTimeout = location.state?.reason === 'idle_timeout';

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      if (mode === 'tenant') {
        await portalLogin(email, password);
        navigate('/portal');
      } else {
        await login(email, password);
        navigate('/');
      }
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col bg-white" data-testid="login-page">
      <div className="flex-1 flex">
        {/* Left Side - Form */}
        <div className="flex-1 flex items-center justify-center p-8 bg-white">
        <div className="w-full max-w-md">
          <div className="mb-10">
            <div className="flex items-center gap-3 mb-8">
              <Buildings weight="bold" className="w-8 h-8 text-slate-900" />
              <span className="font-heading text-xl font-black tracking-tight">IMMO VERWALTER</span>
            </div>
            <h1 className="font-heading text-4xl font-black tracking-tight text-slate-900 mb-2">
              Willkommen zurück
            </h1>
            <p className="text-slate-500 text-sm">
              {mode === 'tenant' ? 'Mieter-Portal: Melden Sie sich an' : 'Melden Sie sich an, um Ihre Immobilien zu verwalten'}
            </p>
          </div>

          {/* Role Toggle */}
          <div className="grid grid-cols-2 gap-0 mb-6 border border-slate-200">
            <button
              type="button"
              onClick={() => setMode('admin')}
              className={`py-3 text-sm font-medium transition-colors ${mode === 'admin' ? 'bg-slate-900 text-white' : 'bg-white text-slate-600 hover:bg-slate-50'}`}
              data-testid="login-mode-admin"
            >
              Vermieter
            </button>
            <button
              type="button"
              onClick={() => setMode('tenant')}
              className={`py-3 text-sm font-medium transition-colors ${mode === 'tenant' ? 'bg-slate-900 text-white' : 'bg-white text-slate-600 hover:bg-slate-50'}`}
              data-testid="login-mode-tenant"
            >
              Mieter
            </button>
          </div>

          {idleTimeout && (
            <div className="mb-4 bg-amber-50 border border-amber-200 text-amber-900 px-3 py-2.5 text-sm flex items-center gap-2 rounded" data-testid="idle-timeout-notice">
              <Hourglass weight="fill" className="w-4 h-4 flex-shrink-0" />
              <span>Sie wurden aus Sicherheitsgründen wegen Inaktivität abgemeldet.</span>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-6">
            {error && (
              <div className="bg-red-50 border border-red-200 text-red-800 px-4 py-3 text-sm" data-testid="login-error">
                {error}
              </div>
            )}

            <div>
              <label className="block text-xs tracking-widest uppercase font-bold text-slate-500 mb-2">
                E-Mail
              </label>
              <div className="relative">
                <EnvelopeSimple className="absolute left-0 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="w-full border-b border-slate-300 bg-transparent pl-8 pr-0 py-3 focus:border-slate-900 focus:ring-0 outline-none transition-colors text-slate-900"
                  placeholder="ihre@email.de"
                  required
                  data-testid="login-email-input"
                />
              </div>
            </div>

            <div>
              <label className="block text-xs tracking-widest uppercase font-bold text-slate-500 mb-2">
                Passwort
              </label>
              <div className="relative">
                <Lock className="absolute left-0 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                <input
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="w-full border-b border-slate-300 bg-transparent pl-8 pr-0 py-3 focus:border-slate-900 focus:ring-0 outline-none transition-colors text-slate-900"
                  placeholder="••••••••"
                  required
                  data-testid="login-password-input"
                />
              </div>
            </div>

            <button
              type="submit"
              disabled={loading}
              className="w-full bg-slate-900 text-white py-3 px-6 font-medium hover:bg-slate-800 focus:ring-2 focus:ring-slate-400 focus:ring-offset-2 transition-all flex items-center justify-center gap-2 disabled:opacity-50"
              data-testid="login-submit-button"
            >
              {loading ? 'Wird angemeldet...' : 'Anmelden'}
              <ArrowRight weight="bold" className="w-4 h-4" />
            </button>
          </form>

          {mode === 'tenant' ? (
            <p className="mt-8 text-center text-sm text-slate-500">
              Erstmaliger Zugang?{' '}
              <Link to="/portal/register" className="text-slate-900 font-medium hover:underline inline-flex items-center gap-1" data-testid="tenant-register-link">
                <Key weight="bold" className="w-3.5 h-3.5" />
                Mit Einladungscode registrieren
              </Link>
            </p>
          ) : (
            <>
              <p className="mt-8 text-center text-sm text-slate-500">
                Noch kein Konto?{' '}
                <Link to="/register" className="text-slate-900 font-medium hover:underline" data-testid="register-link">
                  Jetzt registrieren
                </Link>
              </p>
              <p className="mt-2 text-center">
                <Link to="/forgot-password" className="text-sm text-slate-500 hover:text-slate-900 hover:underline" data-testid="forgot-password-link">
                  Passwort vergessen?
                </Link>
              </p>
            </>
          )}
        </div>
      </div>

      {/* Right Side - Image */}
      <div className="hidden lg:block lg:flex-1 relative">
        <img
          src="https://images.unsplash.com/photo-1758448511648-d7d8e1993c3f?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2MzR8MHwxfHNlYXJjaHwyfHxtb2Rlcm4lMjBhcGFydG1lbnQlMjBidWlsZGluZyUyMGV4dGVyaW9yJTIwbHV4dXJ5fGVufDB8fHx8MTc3NDgxMjkwM3ww&ixlib=rb-4.1.0&q=85"
          alt="Modern Building"
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-slate-900/60 to-transparent" />
        <div className="absolute bottom-12 left-12 right-12 text-white">
          <p className="font-heading text-3xl font-light leading-tight">
            Ihre Immobilien.<br />
            <span className="font-black">Intelligent verwaltet.</span>
          </p>
        </div>
      </div>
      </div>
      <LegalFooter />
    </div>
  );
}
